/**
 * Lightweight Wikipedia-style markdown → HTML for local lore articles.
 * Supports: ## / ### headings, paragraphs, **bold**, *italic*,
 * - / • / 1. lists, --- rules, and simple | pipe tables.
 */

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatInline(text) {
  let parsed = escapeHtml(text);
  // Bold before italic so **...** is not broken by single *
  parsed = parsed.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  parsed = parsed.replace(/\*(.+?)\*/g, '<em>$1</em>');
  return parsed;
}

function isTableSeparator(line) {
  return /^\|?[\s-:|]+\|[\s-:|]+$/.test(line);
}

function splitTableRow(line) {
  let row = line.trim();
  if (row.startsWith('|')) row = row.slice(1);
  if (row.endsWith('|')) row = row.slice(0, -1);
  return row.split('|').map((cell) => cell.trim());
}

function isTableRow(line) {
  return line.includes('|') && splitTableRow(line).length >= 2 && !isTableSeparator(line);
}

/**
 * @param {string} text - Wiki-style markdown body
 * @returns {string} Safe HTML string for dangerouslySetInnerHTML
 */
export function parseWikiArticle(text) {
  if (!text) {
    throw new Error('parseWikiArticle: text is required');
  }

  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const htmlParts = [];
  let paragraphLines = [];
  let listItems = [];
  let listType = null; // 'ul' | 'ol'
  let tableRows = [];

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;
    const content = formatInline(paragraphLines.join(' ').trim());
    if (content) {
      htmlParts.push(`<p>${content}</p>`);
    }
    paragraphLines = [];
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    const tag = listType || 'ul';
    const items = listItems.map((item) => `<li>${formatInline(item)}</li>`).join('');
    htmlParts.push(`<${tag}>${items}</${tag}>`);
    listItems = [];
    listType = null;
  };

  const flushTable = () => {
    if (tableRows.length === 0) return;
    const [header, ...body] = tableRows;
    const headerHtml = header
      .map((cell) => `<th>${formatInline(cell)}</th>`)
      .join('');
    const bodyHtml = body
      .map(
        (row) =>
          `<tr>${row.map((cell) => `<td>${formatInline(cell)}</td>`).join('')}</tr>`
      )
      .join('');
    htmlParts.push(
      `<table class="lore-wiki-table"><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`
    );
    tableRows = [];
  };

  const pushListItem = (type, itemText) => {
    flushTable();
    flushParagraph();
    if (listType && listType !== type) {
      flushList();
    }
    listType = type;
    listItems.push(itemText);
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trimEnd();
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      flushTable();
      flushParagraph();
      continue;
    }

    if (/^---+$/.test(trimmed)) {
      flushList();
      flushTable();
      flushParagraph();
      htmlParts.push('<hr class="lore-wiki-rule" />');
      continue;
    }

    const h2Match = trimmed.match(/^##\s+(.+)$/);
    if (h2Match) {
      flushList();
      flushTable();
      flushParagraph();
      htmlParts.push(`<h2 class="lore-wiki-h2">${formatInline(h2Match[1])}</h2>`);
      continue;
    }

    const h3Match = trimmed.match(/^###\s+(.+)$/);
    if (h3Match) {
      flushList();
      flushTable();
      flushParagraph();
      htmlParts.push(`<h3 class="lore-wiki-h3">${formatInline(h3Match[1])}</h3>`);
      continue;
    }

    if (isTableRow(trimmed) || (tableRows.length > 0 && isTableSeparator(trimmed))) {
      flushList();
      flushParagraph();
      if (isTableSeparator(trimmed)) {
        continue;
      }
      tableRows.push(splitTableRow(trimmed));
      continue;
    }

    const unorderedMatch = trimmed.match(/^[-•]\s+(.+)$/);
    if (unorderedMatch) {
      pushListItem('ul', unorderedMatch[1]);
      continue;
    }

    const orderedMatch = trimmed.match(/^\d+\.\s+(.+)$/);
    if (orderedMatch) {
      pushListItem('ol', orderedMatch[1]);
      continue;
    }

    flushList();
    flushTable();
    paragraphLines.push(trimmed);
  }

  flushList();
  flushTable();
  flushParagraph();

  return htmlParts.join('');
}
